import { useEffect, useState, type FormEvent } from 'react';
import { useCategories } from '@/hooks/useCategories';
import { getCategoryIcon, resolveCategoryIcon } from '@/lib/utils';
import type { CreateCategoryData } from '@/lib/types';
import { Plus, Trash2, Edit2, X, Loader2, FolderOpen } from 'lucide-react';

const PRESET_COLORS = [
  '#6366f1',
  '#8b5cf6',
  '#a855f7',
  '#ec4899',
  '#ef4444',
  '#f97316',
  '#eab308',
  '#22c55e',
  '#14b8a6',
  '#3b82f6',
  '#06b6d4',
  '#f43f5e',
];

export default function CategoriesPage() {
  const {
    categories,
    loading,
    error,
    fetchCategories,
    createCategory,
    updateCategory,
    deleteCategory,
  } = useCategories();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formColor, setFormColor] = useState(PRESET_COLORS[0]);
  const [formIcon, setFormIcon] = useState('');
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  function openCreate() {
    setEditingId(null);
    setFormName('');
    setFormColor(PRESET_COLORS[0]);
    setFormIcon('');
    setFormError('');
    setShowForm(true);
  }

  function openEdit(id: string) {
    const cat = categories.find((c) => c.id === id);
    if (!cat) return;
    setEditingId(id);
    setFormName(cat.name);
    setFormColor(cat.color);
    setFormIcon(cat.icon);
    setFormError('');
    setShowForm(true);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setFormError('');
    setFormLoading(true);

    try {
      const data: CreateCategoryData = {
        name: formName,
        color: formColor,
        icon: formIcon || getCategoryIcon(formName),
      };

      if (editingId) {
        await updateCategory(editingId, data);
      } else {
        await createCategory(data);
      }

      setShowForm(false);
      setEditingId(null);
      resetForm();
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Failed to save category'
      );
    } finally {
      setFormLoading(false);
    }
  }

  function resetForm() {
    setFormName('');
    setFormColor(PRESET_COLORS[0]);
    setFormIcon('');
    setFormError('');
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return;
    }
    await deleteCategory(id);
  }

  return (
    <div className="animate-fadeIn space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Categories</h1>
          <p className="mt-1 text-gray-400">
            Organize your transactions with categories
          </p>
        </div>
        <button onClick={openCreate} className="btn-primary">
          <Plus size={18} className="mr-2" />
          Add Category
        </button>
      </div>

      {/* Error */}
      {error && (
        <div className="rounded-lg bg-accent-red/10 px-4 py-3 text-sm text-accent-red">
          {error}
        </div>
      )}

      {/* Categories Grid */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-brand-500" />
        </div>
      ) : categories.length === 0 ? (
        <div className="card py-12 text-center">
          <FolderOpen className="mx-auto h-12 w-12 text-gray-600" />
          <p className="mt-4 text-gray-500">No categories yet</p>
          <button onClick={openCreate} className="btn-primary mt-4">
            <Plus size={18} className="mr-2" />
            Create your first category
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {categories.map((category) => (
            <div
              key={category.id}
              className="card-hover group flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 items-center justify-center rounded-lg text-lg"
                  style={{ backgroundColor: `${category.color}20` }}
                >
                  {resolveCategoryIcon(category.icon, category.name, category.color)}
                </div>
                <div>
                  <p className="font-medium text-white">{category.name}</p>
                  <div className="mt-0.5 flex items-center gap-2">
                    <div
                      className="h-2 w-2 rounded-full"
                      style={{ backgroundColor: category.color }}
                    />
                    <span className="text-xs text-gray-500">
                      {category.isDefault ? 'Default' : 'Custom'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 transition-opacity group-hover:opacity-100">
                <button
                  onClick={() => openEdit(category.id)}
                  className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-brand-500/10 hover:text-brand-400"
                >
                  <Edit2 size={16} />
                </button>
                {!category.isDefault && (
                  <button
                    onClick={() => handleDelete(category.id)}
                    className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-accent-red/10 hover:text-accent-red"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Category Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="card w-full max-w-md animate-slideUp">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white">
                {editingId ? 'Edit Category' : 'Add Category'}
              </h2>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  resetForm();
                }}
                className="rounded-lg p-1 text-gray-400 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {formError && (
                <div className="rounded-lg bg-accent-red/10 px-4 py-3 text-sm text-accent-red">
                  {formError}
                </div>
              )}

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Name
                </label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="e.g. Groceries"
                  required
                  className="input-base"
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Color
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_COLORS.map((color) => (
                    <button
                      key={color}
                      type="button"
                      onClick={() => setFormColor(color)}
                      className={`h-8 w-8 rounded-full transition-transform ${
                        formColor === color
                          ? 'scale-110 ring-2 ring-white ring-offset-2 ring-offset-surface'
                          : 'hover:scale-105'
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-300">
                  Icon (emoji, optional)
                </label>
                <input
                  type="text"
                  value={formIcon}
                  onChange={(e) => setFormIcon(e.target.value)}
                  placeholder="Auto-detected from name"
                  className="input-base"
                  maxLength={4}
                />
                {formName && !formIcon && (
                  <p className="mt-1 text-xs text-gray-500">
                    Will use: {getCategoryIcon(formName)}
                  </p>
                )}
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    setEditingId(null);
                    resetForm();
                  }}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="btn-primary flex-1"
                >
                  {formLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : editingId ? (
                    'Update Category'
                  ) : (
                    'Add Category'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
